import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Navbar from './Navbar';
import Footer from './Footer';
import { trackBlogView, trackPageView } from '../utils/tracking';

const BlogSolarModels = () => {
  useEffect(() => {
    trackBlogView('solar-models-explained', '5 Solar Procurement Models for Indian Businesses: CAPEX vs OPEX Guide');
    trackPageView('/blogs/solar-models-explained', 'Solar Models Explained');
  }, []);

  useEffect(() => {
    // SEO optimization - Update meta tags for this blog post
    document.title = '5 Solar Procurement Models for Indian Businesses: CAPEX vs OPEX Guide | Lunar Electric';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Complete guide to 5 solar procurement models for Indian businesses: CAPEX, OPEX, RESCO, Group Captive, and Third Party PPA. Choose the right model for your commercial solar needs.');
    }
    
    // Update meta keywords with extensive question variations
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'solar procurement models, solar procurement models India, solar procurement models commercial, solar procurement models business, CAPEX vs OPEX solar, CAPEX vs OPEX solar India, CAPEX solar model, OPEX solar model, RESCO model solar, RESCO model solar India, RESCO solar meaning, RESCO solar full form, RESCO solar benefits, RESCO solar cost, group captive solar, group captive solar India, group captive solar model, group captive solar benefits, solar PPA models, solar PPA India, third party PPA solar, solar PPA agreement, solar PPA meaning, solar power purchase agreement, commercial solar procurement, commercial solar models, commercial solar financing models, commercial solar financing options, commercial solar CAPEX model, commercial solar OPEX model, commercial solar RESCO model, commercial solar group captive, commercial solar PPA, solar investment model, solar financing model, solar ownership model, solar lease model, solar rental model, zero investment solar, zero upfront cost solar, pay per use solar, solar CAPEX advantages, solar CAPEX disadvantages, solar OPEX advantages, solar OPEX disadvantages, RESCO solar advantages, RESCO solar disadvantages, group captive solar advantages, solar PPA advantages, which solar model best, best solar procurement model, solar model comparison, solar model selection, solar model choose, solar model which one, solar model how to choose, solar model differences, solar model explained, solar model guide, solar model India');
    }
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', '5 Solar Procurement Models for Indian Businesses: CAPEX vs OPEX Guide');
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Complete guide to 5 solar procurement models: CAPEX, OPEX, RESCO, Group Captive, and Third Party PPA. Choose the right model for your business needs.');
    }
    
    // Update OG image for this blog post
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', 'https://lunarelectric.in/img/card3.png');
    }
    
    const ogImageSecure = document.querySelector('meta[property="og:image:secure_url"]');
    if (ogImageSecure) {
      ogImageSecure.setAttribute('content', 'https://lunarelectric.in/img/card3.png');
    }
    
    // Update Twitter image
    const twitterImage = document.querySelector('meta[name="twitter:image"]');
    if (twitterImage) {
      twitterImage.setAttribute('content', 'https://lunarelectric.in/img/card3.png');
    }
    
    // Update OG URL for this blog post
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://lunarelectric.in/blogs/solar-models-explained');
    }
    
    // Update canonical URL
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', 'https://lunarelectric.in/blogs/solar-models-explained');
    
    // Add structured data for blog post
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "5 Solar Procurement Models for Indian Businesses: CAPEX vs OPEX Guide",
      "description": "Complete guide to 5 solar procurement models for Indian businesses: CAPEX, OPEX, RESCO, Group Captive, and Third Party PPA models explained.",
      "image": "https://lunarelectric.in/img/card3.png",
      "author": {
        "@type": "Organization",
        "name": "Lunar Electric"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Lunar Electric",
        "logo": {
          "@type": "ImageObject",
          "url": "https://lunarelectric.in/logo512.png"
        }
      },
      "datePublished": "2025-01-27",
      "dateModified": "2025-01-27",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://lunarelectric.in/blogs/solar-models-explained"
      }
    });
    document.head.appendChild(script);

    // Add FAQ structured data
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What are solar procurement models?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Solar procurement models are different ways businesses can adopt solar energy, each with different ownership structures, payment terms, and financial implications. The main models are CAPEX (Capital Expenditure - you own the system), OPEX (Operational Expenditure - pay for electricity), RESCO (Renewable Energy Service Company - third party owns and operates), Group Captive (shared ownership), and Third Party PPA (Power Purchase Agreement - buy power from solar developer). Each model suits different business needs and cash flow situations."
          }
        },
        {
          "@type": "Question",
          "name": "CAPEX vs OPEX solar model",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "CAPEX solar model means you invest capital upfront to own the system, get 40-80% accelerated depreciation tax benefits, own all electricity generated, and benefit long-term after payback. OPEX solar model means zero upfront investment, you pay monthly for electricity consumed at lower rates than grid, the developer owns and maintains the system, and you avoid capital investment. CAPEX suits businesses with capital and wanting ownership. OPEX suits businesses preferring zero upfront cost and operational expenses."
          }
        },
        {
          "@type": "Question",
          "name": "What is RESCO model in solar?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "RESCO (Renewable Energy Service Company) model in solar means a third-party company invests, owns, installs, operates, and maintains the solar system on your property. You only pay for the electricity generated at agreed rates (typically 10-30% lower than grid tariffs) through a Power Purchase Agreement (PPA). You have zero upfront investment, no maintenance responsibilities, and long-term fixed electricity rates. The RESCO company benefits from selling electricity and tax depreciation benefits."
          }
        },
        {
          "@type": "Question",
          "name": "Which solar procurement model is best?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The best solar procurement model depends on your business: CAPEX is best if you have capital and want maximum long-term savings and tax benefits. OPEX/RESCO is best if you want zero upfront cost and prefer operational expenses. Group Captive is best for multiple businesses wanting shared benefits. Choose CAPEX for maximum ROI and ownership, OPEX/RESCO for cash flow flexibility, and Group Captive for shared investment models. Consult a solar expert to determine the best fit for your specific situation."
          }
        },
        {
          "@type": "Question",
          "name": "Solar PPA model explained",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Solar PPA (Power Purchase Agreement) model means a solar developer installs and owns a system on your property, and you agree to purchase electricity from them at a fixed rate (usually lower than grid tariffs) for 15-25 years. You have zero upfront investment, no maintenance costs, guaranteed electricity rates, and simplified adoption. The developer owns the system, gets tax benefits, and sells you power. This is ideal for businesses wanting solar benefits without capital investment."
          }
        },
        {
          "@type": "Question",
          "name": "Group captive solar model",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Group Captive solar model allows multiple businesses or entities to collectively invest in a solar plant and share the generated electricity based on their investment ratio. It enables smaller businesses to access solar benefits through shared ownership, allows pooling of investments for larger systems, provides cost-sharing advantages, and distributes electricity proportionally. This model suits industrial clusters, business parks, or multiple companies wanting to share solar investment and benefits."
          }
        },
        {
          "@type": "Question",
          "name": "Solar CAPEX model benefits",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Solar CAPEX model benefits include full system ownership, maximum long-term savings after payback period, 40-80% accelerated depreciation tax benefits in first year, complete control over the system, ability to sell excess electricity, asset value on balance sheet, and highest ROI over 25-year system life. After 3-6 year payback, you get nearly free electricity for 20+ years, making it the most profitable model for businesses with capital availability."
          }
        },
        {
          "@type": "Question",
          "name": "Solar OPEX model benefits",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Solar OPEX model benefits include zero upfront capital investment, immediate electricity cost savings (10-30% lower than grid), no maintenance responsibilities, predictable monthly costs, no balance sheet impact, flexibility to upgrade or modify, and simplified adoption process. This model is ideal for businesses preferring operational expenses over capital expenditure, maintaining cash flow, and wanting solar benefits without ownership responsibilities."
          }
        }
      ]
    });
    document.head.appendChild(faqScript);

    // Add BreadcrumbList structured data
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://lunarelectric.in/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Blogs",
          "item": "https://lunarelectric.in/blogs"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "Solar Procurement Models",
          "item": "https://lunarelectric.in/blogs/solar-models-explained"
        }
      ]
    });
    document.head.appendChild(breadcrumbScript);
    
    // Scroll to top
    window.scrollTo(0, 0);
    
    return () => {
      // Cleanup - remove the added scripts
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        if (script.textContent.includes('BlogPosting') || script.textContent.includes('FAQPage') || script.textContent.includes('BreadcrumbList')) {
          script.remove();
        }
      });
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>5 Solar Procurement Models for Indian Businesses: CAPEX vs OPEX Guide | Lunar Electric</title>
        <meta name="description" content="Complete guide to 5 solar procurement models for Indian businesses: CAPEX, OPEX, RESCO, Group Captive, and Third Party PPA. Choose the right model for your commercial solar needs." />
        <meta property="og:title" content="5 Solar Procurement Models for Indian Businesses: CAPEX vs OPEX Guide" />
        <meta property="og:description" content="Complete guide to 5 solar procurement models: CAPEX, OPEX, RESCO, Group Captive, and Third Party PPA. Choose the right model for your business needs." />
        <meta property="og:image" content="https://lunarelectric.in/img/card3.png" />
        <meta property="og:url" content="https://lunarelectric.in/blogs/solar-models-explained" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="5 Solar Procurement Models for Indian Businesses: CAPEX vs OPEX Guide" />
        <meta name="twitter:description" content="Complete guide to 5 solar procurement models for Indian businesses" />
        <meta name="twitter:image" content="https://lunarelectric.in/img/card3.png" />
        <link rel="canonical" href="https://lunarelectric.in/blogs/solar-models-explained" />
      </Helmet>
      <Navbar />
      <a href="#main-content" className="skip-to-main" style={{position: 'absolute', left: '-9999px', zIndex: 9999}} onFocus={(e) => e.target.style.left = '0'}>Skip to main content</a>
      <div className="blog-post-page" id="main-content">
        {/* Blog Header */}
        <div className="blog-hero">
          <img 
            src="/img/card3.png" 
            alt="5 Solar Procurement Models for Indian Businesses by Lunar Electric - Complete CAPEX vs OPEX Guide"
            title="Lunar Electric Solar Models Explanation"
            loading="eager"
            width="1200"
            height="630"
          />
          <div className="blog-overlay">
            <div className="container">
              <h1>5 Solar Procurement Models for Indian Businesses: CAPEX vs OPEX Guide</h1>
              <div className="blog-meta">
                <span>Published: January 27, 2025</span>
                <span>Category: Commercial Solar</span>
                <span>⏱️ 11 min read</span>
              </div>
            </div>
          </div>
        </div>

        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="blog-breadcrumb">
          <div className="container">
            <Link to="/" aria-label="Home">Home</Link> / <Link to="/blogs" aria-label="Blogs">Blogs</Link> / <span aria-current="page">Solar Procurement Models</span>
          </div>
        </nav>

        {/* Blog Content */}
        <div className="blog-content-wrapper">
          <div className="container">
            <div className="blog-article">
              <div className="blog-intro">
                <p>
                  Choosing the right solar procurement model is one of the most critical decisions for Indian businesses 
                  adopting solar energy. Different models offer varying levels of capital involvement, risk transfer, 
                  control, and cost optimization.
                </p>
                <p>
                  This comprehensive guide breaks down the five most commonly used solar procurement models in India, 
                  categorized into on-site and off-site approaches. Each model is explained with its practical pros, 
                  risks, and fitment considerations to help you make an informed decision for your business.
                </p>
              </div>

              <h2>1. On-Site Models</h2>
              <p>
                On-site solar models involve installing the solar system at your own premises—whether on rooftops, 
                ground-mounted structures, or other available space at your facility. These models offer direct access 
                to solar power and typically provide the highest savings.
              </p>

              <h3>a) CAPEX Model</h3>
              <p>
                <strong>What it is:</strong> The business buys and owns the solar plant installed at its premises (sheet roof, 
                RCC roof, or ground using MS/super structures). You pay upfront or via loan and book the asset. Typical practice 
                includes 1-year free O&M, then AMC or in-house upkeep thereafter.
              </p>
              <p>
                <strong>Cash flow:</strong> Upfront capex; operating savings accrue through avoided grid tariffs for 20–25 years. 
                Payback depends on tariff, utilization, and net metering/open access rules in your state.
              </p>
              <p>
                <strong>Advantages:</strong>
              </p>
              <ul>
                <li>Lowest levelized cost over life</li>
                <li>Complete asset control</li>
                <li>Accelerated depreciation benefits where applicable</li>
                <li>Complete control over O&M standards and uptime priorities</li>
                <li>Maximum return on investment over the system's lifetime</li>
              </ul>

              <h3>b) OPEX (RESCO/PPA) Model</h3>
              <p>
                <strong>What it is:</strong> A developer invests, builds, and operates the on-site plant. You pay per kWh generated 
                at an agreed tariff (often in the ₹4–₹6/unit band depending on size, location, and risk) while the developer handles 
                capex and O&M end-to-end.
              </p>
              <p>
                <strong>Cash flow:</strong> Zero/low upfront investment; pay-as-you-generate with contracted tariff escalation terms, 
                minimum offtake clauses, and performance guarantees embedded in the agreement.
              </p>
              <p>
                <strong>Advantages:</strong>
              </p>
              <ul>
                <li>No capital expenditure required</li>
                <li>Performance risk transferred to developer</li>
                <li>Predictable tariff hedge against rising grid prices</li>
                <li>Easier internal approvals for many corporates</li>
                <li>Developer handles all operational responsibilities</li>
              </ul>
              <p>
                <strong>Best for:</strong> Capacities above 250 kWp where you want solar benefits without capital commitment
              </p>

              <h2>2. Off-Site Models</h2>
              <p>
                Off-site models involve installing solar systems at locations separate from your consumption point, with power 
                transmitted through the grid via open access. These models offer flexibility to source from optimal solar resource 
                zones while consuming at industrial locations.
              </p>

              <h3>a) PPA – Group Captive Model</h3>
              <p>
                <strong>What it is:</strong> Multiple consumers (or a single anchor plus others) take power from a developer's 
                off-site plant through open access. Each consumer must hold at least 26% equity in the project SPV and consume at 
                least 51% of the power to qualify as captive under Indian regulations.
              </p>
              <p>
                <strong>Cash flow:</strong> Equity participation plus per-unit tariff. Typically avoids specific cross-subsidy 
                surcharges where captive conditions are met, improving landed cost predictability versus discom tariffs.
              </p>
              <p>
                <strong>Advantages:</strong>
              </p>
              <ul>
                <li>Significant tariff reduction potential for high-load facilities</li>
                <li>Portfolio aggregation flexibility</li>
                <li>Regulatory recognition of captive status when thresholds are maintained</li>
                <li>Lower effective power cost due to captive benefits</li>
              </ul>
              <p>
                <strong>Best for:</strong> Capacities above 2 MWp where you can meet the 26% equity and 51% consumption requirements
              </p>

              <h3>b) PPA – Third Party (Non-captive) Model</h3>
              <p>
                <strong>What it is:</strong> Developer owns and operates an off-site plant and sells power via open access to the 
                consumer at a contracted tariff. No 26% equity requirement for the buyer, akin to an off-site OPEX model.
              </p>
              <p>
                <strong>Cash flow:</strong> Pure per-unit payment with no equity outlay. Landed cost depends on OA wheeling, 
                transmission, and cross-subsidy surcharges applicable in your state/voltage category.
              </p>
              <p>
                <strong>Advantages:</strong>
              </p>
              <ul>
                <li>No capex and no equity investment required</li>
                <li>Fast to contract and implement</li>
                <li>Geographic flexibility to source from the best resource zones while consuming at industrial locations</li>
                <li>Developer takes on all project development and operational risk</li>
              </ul>
              <p>
                <strong>Watchouts:</strong> Exposure to policy changes in OA charges and banking, curtailment risk in stressed grids, 
                and contract clauses on availability, deemed generation, and change-in-law should be carefully reviewed.
              </p>

              <h3>c) CAPEX (Dedicated Solar Park)</h3>
              <p>
                <strong>What it is:</strong> The consumer funds or acquires a developer-built, dedicated solar park. Ownership 
                transfers entirely to the consumer, who then operates it (directly or via outsourced O&M) and wheels power through 
                open access to facilities.
              </p>
              <p>
                <strong>Cash flow:</strong> Large upfront capex plus recurring OA charges. Benefits from complete asset control and 
                potential cost optimization at scale across multiple plants/locations.
              </p>
              <p>
                <strong>Advantages:</strong>
              </p>
              <ul>
                <li>Lowest lifetime cost potential at scale</li>
                <li>Complete control on technology and O&M</li>
                <li>Strategic hedge for multi-site portfolios</li>
                <li>Ability to tailor augmentation (e.g., storage) over time</li>
                <li>Maximum flexibility for future expansion or modifications</li>
              </ul>
              <p>
                <strong>Best for:</strong> Capacities above 5 MWp where scale economies kick in and you want maximum control and 
                lowest long-term costs
              </p>

              <div className="checklist-box">
                <h3>How to Choose: Quick Fit Matrix</h3>
                <ul>
                  <li>
                    <strong>Capital-light and quick:</strong> On-site OPEX or Off-site Third Party when balance-sheet neutrality 
                    and speed matter more than absolute lowest cost
                  </li>
                  <li>
                    <strong>Tariff certainty with regulatory guardrails:</strong> Group Captive for buyers able to invest 26% equity 
                    and maintain captive conditions to mitigate surcharges over long tenors
                  </li>
                  <li>
                    <strong>Lowest lifetime cost with complete control:</strong> On-site CAPEX for strong rooftops or Off-site CAPEX 
                    for multi-MW portfolios needing scale and customization
                  </li>
                </ul>
              </div>

              <h2>Key Decision Factors</h2>
              <p>
                When choosing the right solar procurement model for your business, consider these critical factors:
              </p>
              <ul>
                <li><strong>Available Capital:</strong> Can you invest upfront, or do you need a zero-capex solution?</li>
                <li><strong>Risk Appetite:</strong> How much operational and performance risk are you willing to assume?</li>
                <li><strong>Energy Requirements:</strong> What's your load profile and capacity need?</li>
                <li><strong>Regulatory Environment:</strong> What open access and captive regulations apply in your state?</li>
                <li><strong>Long-term Strategy:</strong> Do you want ownership or a service model?</li>
                <li><strong>O&M Preferences:</strong> Can you manage operations in-house or prefer outsourced maintenance?</li>
              </ul>

              <h2>Next Steps</h2>
              <p>
                Ready to explore which solar procurement model is best for your business? <Link to="/solar-calculator" className="inline-link">Use our commercial solar calculator</Link> to estimate your potential savings across different models. For a detailed feasibility study and customized proposal, <Link to="/callback" className="inline-link">contact our commercial solar experts</Link> who specialize in all these procurement models.
              </p>
              <p>
                At Lunar Electric, we've helped hundreds of businesses across India choose and implement the right solar procurement model, from small retailers to large manufacturing units. Our end-to-end solutions include feasibility studies, model selection guidance, financing assistance, installation, and ongoing maintenance support across all procurement models.
              </p>

              <div className="blog-related-posts">
                <h3>Related Articles</h3>
                <div className="related-posts-grid">
                  <Link to="/blogs/rooftop-solar-guide" className="related-post-card">
                    <h4>Rooftop Solar in India: Complete Guide</h4>
                    <p>Step-by-step guide to installing rooftop solar at home</p>
                  </Link>
                  <Link to="/blogs/commercial-solar-benefits" className="related-post-card">
                    <h4>Why Businesses Should Adopt Solar in India</h4>
                    <p>Complete guide on commercial solar benefits and ROI</p>
                  </Link>
                </div>
              </div>

              <div className="blog-cta-box">
                <h3>Ready to Go Solar?</h3>
                <p>Get a free consultation and discover how much you can save with solar energy</p>
                <Link to="/callback" className="btn-primary">Get Free Quote</Link>
                <Link to="/solar-calculator" className="btn-secondary">Calculate Savings</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BlogSolarModels;
